// src/components/nutrition/minerals/nutritionMinerals.jsx

const MINERAL_LABELS = {
  calcium: "Calcium",
  copper: "Copper",
  fluoride: "Fluoride",
  iodine: "Iodine",
  iron: "Iron",
  magnesium: "Magnesium",
  manganese: "Manganese",
  molybdenum: "Molybdenum",
  phosphorus: "Phosphorus",
  potassium: "Potassium",
  potassiumSodium: "Potassium & Sodium (K:Na)",
  selenium: "Selenium",
  sodium: "Sodium",
  zinc: "Zinc",
};

export const MINERAL_IDS = Object.keys(MINERAL_LABELS);

const MINERAL_OVERVIEW_IDS = [
  "calcium",
  "copper",
  "fluoride",
  "iodine",
  "iron",
  "magnesium",
];

export const mineralOptions = MINERAL_IDS.map((mineralId) => ({
  id: mineralId,
  label: MINERAL_LABELS[mineralId],
}));

export const mineralInitialData = {
  calcium: {
    todayDV: null,
    avgDV: null,
    mg: null,
    topSource: "Milk",
  },
  copper: {
    todayDV: null,
    avgDV: null,
    mcg: null,
  },
  fluoride: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  iodine: {
    todayDV: null,
    avgDV: null,
    mcg: null,
  },
  iron: {
    todayDV: null,
    avgDV: null,
    mg: null,
    type: "Heme",
  },
  magnesium: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  manganese: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  molybdenum: {
    todayDV: null,
    avgDV: null,
    mcg: null,
  },
  phosphorus: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  potassium: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  potassiumSodium: {
    ratio: null,
    potassium: null,
    sodium: null,
    trend: null,
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  selenium: {
    todayDV: null,
    avgDV: null,
    mcg: null,
  },
  sodium: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  zinc: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
};

export function getMineralsGroup() {
  return {
    name: "Minerals",
    items: mineralOptions,
  };
}

export function getMineralsOverviewMeta(data) {
  const mineralData = data || {};
  const values = MINERAL_OVERVIEW_IDS.map(
    (mineralId) => mineralData[mineralId]?.todayDV ?? 100,
  );

  if (values.length === 0) {
    return { id: "minerals", label: "Minerals", meta: "–" };
  }

  const averageValue = Math.round(
    values.reduce((sum, value) => sum + value, 0) / values.length,
  );

  return { id: "minerals", label: "Minerals", meta: `${averageValue}%` };
}

export function getMineralStatuses(data, { dvStatusFn, knaStatusFn } = {}) {
  const mineralData = data || {};
  const statuses = {};

  const mineralIdsForStatus = MINERAL_IDS.filter(
    (mineralId) => mineralId !== "potassiumSodium",
  );

  mineralIdsForStatus.forEach((mineralId) => {
    statuses[mineralId] = dvStatusFn?.(mineralData[mineralId]?.todayDV);
  });

  const potassiumSodiumNode = mineralData.potassiumSodium;
  if (potassiumSodiumNode && potassiumSodiumNode.todayDV != null) {
    // When we have a pseudo-%DV for K:Na, use the regular DV status logic
    // so it behaves like other nutrients.
    statuses.potassiumSodium = dvStatusFn?.(potassiumSodiumNode.todayDV);
  } else {
    // Fallback: derive status directly from the K:Na ratio.
    statuses.potassiumSodium = knaStatusFn?.(
      potassiumSodiumNode?.ratio ?? Number.NaN,
    );
  }

  return statuses;
}

export function getMineralFocusEntries(data) {
  const mineralData = data || {};
  const entries = [];

  const mineralIdsForFocus = MINERAL_IDS.filter(
    (mineralId) => mineralId !== "potassiumSodium",
  );

  mineralIdsForFocus.forEach((mineralId) => {
    entries.push({
      id: mineralId,
      score: mineralData[mineralId]?.todayDV ?? 100,
    });
  });

  entries.push({
    id: "potassiumSodium",
    score:
      mineralData.potassiumSodium?.todayDV ??
      (mineralData.potassiumSodium?.ratio ?? 2) * 50,
  });

  return entries;
}

// ---------- Helpers used by special tile + snapshot builder ----------

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Map status → ring stroke color so K:Na visually matches other tiles.
 */
function getStrokeColorForStatus(status) {
  switch (status) {
    case "low":
      // Amber-ish (warn)
      return "#f59e0b";
    case "high":
    case "very-high":
      // Red-ish (danger)
      return "#ef4444";
    case "data-needed":
      // Muted gray
      return "#6b7280";
    default:
      // OK / unknown → emerald-ish green
      return "#10b981";
  }
}

/**
 * Map a K:Na ratio to a pseudo-%DV so the ring + DV thresholds
 * can treat it like any other nutrient.
 *
 * Design:
 * - Ideal ratio ~ 2:1 → 100%.
 * - Ratios below 1:1 → < 50%.
 * - Ratios above 4:1 are capped at 200%.
 */
function computeKnaDvPercentFromRatio(ratio) {
  if (typeof ratio !== "number" || !Number.isFinite(ratio) || ratio <= 0) {
    return null;
  }

  const idealRatio = 2; // 2:1
  const maxRatio = 4; // cap at 4:1 → 200%
  const normalized = Math.min(ratio, maxRatio) / idealRatio;
  let dvPercent = Math.round(normalized * 100);

  if (!Number.isFinite(dvPercent)) {
    return null;
  }

  if (dvPercent < 0) dvPercent = 0;
  if (dvPercent > 200) dvPercent = 200;

  return dvPercent;
}

// ---------- Special K:Na tile ----------

export function buildMineralSpecialTile(mineralId, node, statuses, label) {
  // All standard minerals use the default DV-based tile.
  if (mineralId !== "potassiumSodium") {
    return null;
  }

  // Custom tile for K:Na ratio, but using the same ring layout as other minerals.
  if (!node || typeof node !== "object") {
    return null;
  }

  if (typeof node.ratio !== "number" || !Number.isFinite(node.ratio)) {
    return null;
  }

  const ratioRaw = node.ratio;
  const ratioRounded = Math.round(ratioRaw * 10) / 10;
  const ratioLabel = `${ratioRounded}:1`;

  const potassiumMg =
    typeof node.potassium === "number" && Number.isFinite(node.potassium)
      ? Math.round(node.potassium)
      : null;

  const sodiumMg =
    typeof node.sodium === "number" && Number.isFinite(node.sodium)
      ? Math.round(node.sodium)
      : null;

  const daysInRange =
    typeof node.rangeDays === "number" &&
    Number.isFinite(node.rangeDays) &&
    node.rangeDays > 0
      ? node.rangeDays
      : null;

  const pseudoDvPercent =
    typeof node.todayDV === "number" && Number.isFinite(node.todayDV)
      ? node.todayDV
      : computeKnaDvPercentFromRatio(ratioRaw);

  const safePct =
    typeof pseudoDvPercent === "number" && Number.isFinite(pseudoDvPercent)
      ? pseudoDvPercent
      : 0;

  const subText = daysInRange
    ? `Average K:Na ratio over ${daysInRange} days`
    : "Average K:Na ratio";

  let helper = null;
  if (potassiumMg != null && sodiumMg != null) {
    helper = `Approximate intakes: ${potassiumMg} mg potassium vs ${sodiumMg} mg sodium per day (average over the selected dates).`;
  } else if (potassiumMg != null) {
    helper = `Approximate intake: ${potassiumMg} mg potassium per day (average over the selected dates).`;
  } else if (sodiumMg != null) {
    helper = `Approximate intake: ${sodiumMg} mg sodium per day (average over the selected dates).`;
  }

  const status = statuses?.[mineralId] ?? null;
  const strokeColor = getStrokeColorForStatus(status);

  // No "details" object here – that is what adds the chevron button and
  // expandable block. We keep it consistent with other minerals:
  // ring hero + helper text only.
  return {
    id: mineralId,
    label,
    status,
    hero: {
      type: "ring",
      pct: safePct,
      ariaLabel: `${label} ${safePct}%`,
      mainText: ratioLabel,
      subText,
      stroke: strokeColor,
    },
    helper,
    details: null,
  };
}

// ---------- Chart + labels ----------

export function getMineralChartData(data, { low = 80, high = 120 } = {}) {
  const mineralData = data || {};
  const lowMinerals = [];
  const onGoalMinerals = [];
  const highMinerals = [];

  const chartIds = MINERAL_IDS.filter(
    (mineralId) => mineralId !== "potassiumSodium",
  );

  chartIds.forEach((mineralId) => {
    const nutrient = mineralData[mineralId];
    const dailyValuePercent = nutrient?.todayDV;

    if (!Number.isFinite(dailyValuePercent)) return;

    const label = MINERAL_LABELS[mineralId];

    const isLow = dailyValuePercent <= low;
    const isHigh = dailyValuePercent > high;

    if (isLow) {
      lowMinerals.push({ id: mineralId, label, dv: dailyValuePercent });
    } else if (isHigh) {
      highMinerals.push({ id: mineralId, label, dv: dailyValuePercent });
    } else {
      onGoalMinerals.push({ id: mineralId, label, dv: dailyValuePercent });
    }
  });

  const potassiumSodiumNode = mineralData.potassiumSodium;
  if (potassiumSodiumNode && potassiumSodiumNode.todayDV != null) {
    const label = MINERAL_LABELS.potassiumSodium;
    const dailyValuePercent = potassiumSodiumNode.todayDV;

    if (dailyValuePercent <= low) {
      lowMinerals.push({
        id: "potassiumSodium",
        label,
        dv: dailyValuePercent,
      });
    } else if (dailyValuePercent > high) {
      highMinerals.push({
        id: "potassiumSodium",
        label,
        dv: dailyValuePercent,
      });
    } else {
      onGoalMinerals.push({
        id: "potassiumSodium",
        label,
        dv: dailyValuePercent,
      });
    }
  }

  return {
    low: lowMinerals,
    onGoal: onGoalMinerals,
    high: highMinerals,
    category: "minerals",
    categoryLabel: "Minerals",
  };
}

export function getMineralIdsForChart() {
  return MINERAL_IDS.filter((mineralId) => mineralId !== "potassiumSodium");
}

export function getMineralLabel(mineralId) {
  return MINERAL_LABELS[mineralId] || mineralId;
}

// ---------- Snapshot → mineral data ----------

const MINERAL_SNAPSHOT_META = {
  calcium: { baseKey: "calcium", amountField: "mg" },
  phosphorus: { baseKey: "phosphorus", amountField: "mg" },
  magnesium: { baseKey: "magnesium", amountField: "mg" },
  iron: { baseKey: "iron", amountField: "mg" },
  zinc: { baseKey: "zinc", amountField: "mg" },
  copper: { baseKey: "copper", amountField: "mcg" },
  selenium: { baseKey: "selenium", amountField: "mcg" },
  iodine: { baseKey: "iodine", amountField: "mcg" },
  potassium: { baseKey: "potassium", amountField: "mg" },
  sodium: { baseKey: "salt", amountField: "g", convert: "gToMg" },
  fluoride: { baseKey: "fluoride", amountField: "mg" },
  manganese: { baseKey: "manganese", amountField: "mg" },
  molybdenum: { baseKey: "molybdenum", amountField: "mcg" },
};

function resolveSnapshotKeyForMineral(
  baseKey,
  averageDailyIntake,
  comparisonVsTargets,
) {
  if (!baseKey) {
    return null;
  }

  const comparisonKeys = Object.keys(comparisonVsTargets || {});
  const averageKeys = Object.keys(averageDailyIntake || {});
  const allKeysSet = new Set([...comparisonKeys, ...averageKeys]);
  const allKeys = Array.from(allKeysSet);

  if (allKeys.length === 0) {
    return null;
  }

  const lowerBase = String(baseKey).toLowerCase();

  const matchers = [
    (key) => key.toLowerCase() === lowerBase,
    (key) => key.toLowerCase().startsWith(`${lowerBase}_`),
    (key) =>
      key.toLowerCase().includes(`${lowerBase}_`) ||
      key.toLowerCase().includes(`${lowerBase}-`),
    (key) => key.toLowerCase().includes(lowerBase),
  ];

  for (const match of matchers) {
    const foundKey = allKeys.find(match);
    if (foundKey) {
      return foundKey;
    }
  }

  return null;
}

function createBaseMineralDataFromInitialMeta() {
  const result = {};

  MINERAL_IDS.forEach((mineralId) => {
    const initialMeta = mineralInitialData[mineralId] || {};
    result[mineralId] = { ...initialMeta };
  });

  return result;
}

function mergeMineralDataIntoExisting(existingData, newData) {
  if (!existingData || typeof existingData !== "object") {
    return newData;
  }

  const mergedData = existingData;

  MINERAL_IDS.forEach((mineralId) => {
    const newMineralData = newData[mineralId] || {};
    const existingMineralData = existingData[mineralId] || {};

    mergedData[mineralId] = {
      ...existingMineralData,
      ...newMineralData,
    };
  });

  return mergedData;
}

export function buildMineralDataFromNutritionCalculateSnapshot(
  snapshotOrIntakeSummary,
  existingData,
) {
  // Start with the initial mineral metadata so we always have a full shape.
  const mineralDataMap = createBaseMineralDataFromInitialMeta();

  if (!snapshotOrIntakeSummary) {
    return mergeMineralDataIntoExisting(existingData, mineralDataMap);
  }

  const intakeSummary =
    snapshotOrIntakeSummary && snapshotOrIntakeSummary.intakeSummary
      ? snapshotOrIntakeSummary.intakeSummary
      : snapshotOrIntakeSummary;

  if (!intakeSummary || typeof intakeSummary !== "object") {
    return mergeMineralDataIntoExisting(existingData, mineralDataMap);
  }

  const { averageDailyIntake = {}, comparisonVsTargets = {} } = intakeSummary;

  // Populate elemental minerals (calcium, magnesium, potassium, sodium, etc.).
  Object.entries(MINERAL_SNAPSHOT_META).forEach(
    ([mineralId, { baseKey, amountField, convert }]) => {
      const resolvedKey = resolveSnapshotKeyForMineral(
        baseKey,
        averageDailyIntake,
        comparisonVsTargets,
      );

      if (!resolvedKey) {
        return;
      }

      const comparison = comparisonVsTargets[resolvedKey];
      const averageValue = averageDailyIntake[resolvedKey];

      const actualFromComparison =
        comparison && isFiniteNumber(comparison.actual)
          ? comparison.actual
          : null;

      const actualFromAverage = isFiniteNumber(averageValue)
        ? averageValue
        : null;

      const actual =
        actualFromComparison != null ? actualFromComparison : actualFromAverage;

      const mineralDataBeforeUpdate = mineralDataMap[mineralId] || {};
      const mineralDataForMineral = mineralDataBeforeUpdate;
      mineralDataMap[mineralId] = mineralDataForMineral;

      if (comparison && isFiniteNumber(comparison.percentOfTarget)) {
        const roundedPercent = Math.round(comparison.percentOfTarget);
        mineralDataForMineral.todayDV = roundedPercent;
        mineralDataForMineral.avgDV = roundedPercent;
      }

      if (isFiniteNumber(actual)) {
        if (amountField === "mcg") {
          mineralDataForMineral.mcg = Math.round(actual);
        } else if (amountField === "mg") {
          mineralDataForMineral.mg = Math.round(actual * 10) / 10;
        } else if (amountField === "g" && convert === "gToMg") {
          // Convert grams of salt to milligrams for sodium so it behaves like other minerals.
          mineralDataForMineral.mg = Math.round(actual * 1000);
        } else if (amountField === "g") {
          mineralDataForMineral.g = Math.round(actual * 100) / 100;
        }
      }
    },
  );

  // Attach rangeDays so minerals behave like vitamins for range-aware logic.
  let rangeDays = null;
  if (
    snapshotOrIntakeSummary &&
    typeof snapshotOrIntakeSummary === "object" &&
    snapshotOrIntakeSummary.rangeInfo &&
    typeof snapshotOrIntakeSummary.rangeInfo.daysInRange === "number" &&
    Number.isFinite(snapshotOrIntakeSummary.rangeInfo.daysInRange)
  ) {
    rangeDays = snapshotOrIntakeSummary.rangeInfo.daysInRange;
  }

  if (rangeDays != null) {
    MINERAL_IDS.forEach((mineralId) => {
      const mineralNode =
        mineralDataMap[mineralId] || (mineralDataMap[mineralId] = {});
      mineralNode.rangeDays = rangeDays;
    });
  }

  // Merge with any existing mineral data that may already be in the UI state.
  const finalData = mergeMineralDataIntoExisting(existingData, mineralDataMap);

  // ---- Build composite Potassium & Sodium (K:Na) node --------------------
  // We derive this from the already-resolved potassium and sodium mineral nodes.
  const potassiumNode = finalData.potassium;
  const sodiumNode = finalData.sodium;

  if (
    potassiumNode &&
    isFiniteNumber(potassiumNode.mg) &&
    sodiumNode &&
    isFiniteNumber(sodiumNode.mg) &&
    sodiumNode.mg > 0
  ) {
    const potassiumToSodiumRatio = potassiumNode.mg / sodiumNode.mg;

    const potassiumSodiumExistingNode = finalData.potassiumSodium || {};
    const potassiumSodiumNode = {
      ...potassiumSodiumExistingNode,
      // Store the ratio and the underlying component intakes so
      // knaStatusFn, focus entries, and tiles can all reuse it.
      ratio: potassiumToSodiumRatio,
      potassium: potassiumNode.mg,
      sodium: sodiumNode.mg,
    };

    // Propagate rangeDays if we have it on either side so the tile
    // behaves consistently with other minerals over the selected range.
    if (potassiumNode.rangeDays != null) {
      potassiumSodiumNode.rangeDays = potassiumNode.rangeDays;
    } else if (sodiumNode.rangeDays != null) {
      potassiumSodiumNode.rangeDays = sodiumNode.rangeDays;
    }

    // Derive a pseudo-%DV from the ratio so the hero ring, donut charts,
    // and other DV-based logic can treat K:Na like a nutrient.
    const knaDvPercent = computeKnaDvPercentFromRatio(potassiumToSodiumRatio);
    if (knaDvPercent != null) {
      potassiumSodiumNode.todayDV = knaDvPercent;
      potassiumSodiumNode.avgDV = knaDvPercent;
    }

    finalData.potassiumSodium = potassiumSodiumNode;
  }

  return finalData;
}
